import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ProgressIndicator from '../../components/ui/ProgressIndicator';
import AIProcessingStatus from '../../components/ui/AIProcessingStatus';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import RiskHeatmap from './components/RiskHeatmap';
import ReadinessScore from './components/ReadinessScore';
import SimulationResults from './components/SimulationResults';
import CriticalAlerts from './components/CriticalAlerts';
import RiskTrendChart from './components/RiskTrendChart';
import HiddenRiskDetection from './components/HiddenRiskDetection';

const AIAnalysisDashboard = () => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState('Memuat data penilaian...');
  const [showDashboard, setShowDashboard] = useState(false);

  useEffect(() => {
    const stages = [
      { progress: 15, stage: 'Memuat data penilaian...', duration: 800 },
      { progress: 30, stage: 'Menganalisis data pemasok...', duration: 1000 },
      { progress: 45, stage: 'Mengevaluasi kapasitas operasional...', duration: 1200 },
      { progress: 60, stage: 'Menganalisis kesehatan keuangan...', duration: 1000 },
      { progress: 75, stage: 'Mendeteksi pola risiko tersembunyi...', duration: 1500 },
      { progress: 90, stage: 'Menghitung skor kesiapan scale-up...', duration: 1000 },
      { progress: 100, stage: 'Finalisasi analisis AI...', duration: 800 },
    ];

    let currentStageIndex = 0;

    const processStages = () => {
      if (currentStageIndex < stages?.length) {
        const currentStage = stages?.[currentStageIndex];
        
        setTimeout(() => {
          setProcessingProgress(currentStage?.progress);
          setProcessingStage(currentStage?.stage);
          currentStageIndex++;
          processStages();
        }, currentStage?.duration);
      } else {
        setTimeout(() => {
          setIsProcessing(false);
          setShowDashboard(true);
        }, 500);
      }
    };

    processStages();
  }, []);

  const riskData = [
    {
      id: 'supply',
      category: 'Risiko Pasokan',
      icon: 'Milk',
      score: 72,
      description: 'Ketergantungan tinggi pada 3 pemasok utama dengan fluktuasi pasokan 25% per bulan. Risiko kolaps pasokan dalam 6 bulan jika tidak dimitigasi.'
    },
    {
      id: 'operational',
      category: 'Risiko Operasional',
      icon: 'Settings',
      score: 58,
      description: 'Bottleneck produksi teridentifikasi pada tahap pasteurisasi. Kapasitas maksimal 65% dari target scale-up dengan peralatan existing.'
    },
    {
      id: 'equipment',
      category: 'Risiko Peralatan',
      icon: 'Wrench',
      score: 81,
      description: 'Mesin pasteurisasi berusia 8 tahun dengan maintenance record buruk. Probabilitas breakdown 45% dalam 12 bulan ke depan.'
    },
    {
      id: 'financial',
      category: 'Risiko Keuangan',
      icon: 'DollarSign',
      score: 64,
      description: 'Margin operasional tipis 8% dengan cashflow negatif 3 dari 6 bulan terakhir. Buffer keuangan hanya cukup untuk 2 bulan operasional.'
    },
    {
      id: 'market',
      category: 'Risiko Pasar',
      icon: 'TrendingUp',
      score: 45,
      description: 'Ketergantungan 70% pada 2 buyer institusional. Diversifikasi pasar minimal dengan risiko kehilangan kontrak utama.'
    },
    {
      id: 'governance',
      category: 'Risiko Tata Kelola',
      icon: 'Shield',
      score: 52,
      description: 'Struktur organisasi informal dengan SOP terbatas. Tidak ada sistem quality control terstandar dan dokumentasi proses tidak lengkap.'
    }
  ];

  const readinessBreakdown = [
    { dimension: 'Stabilitas Pasokan', score: 28 },
    { dimension: 'Kapasitas Operasional', score: 42 },
    { dimension: 'Kondisi Peralatan', score: 19 },
    { dimension: 'Kesehatan Keuangan', score: 36 },
    { dimension: 'Diversifikasi Pasar', score: 55 },
    { dimension: 'Sistem Tata Kelola', score: 48 }
  ];

  const simulationData = [
    {
      type: 'supply',
      title: 'Simulasi Kolaps Pasokan',
      probability: 68,
      impactTime: '4-6 bulan',
      estimatedLoss: 'Rp 450 juta',
      chartType: 'line',
      chartData: [
        { period: 'Bulan 1', baseline: 100, scenario: 100 },
        { period: 'Bulan 2', baseline: 100, scenario: 85 },
        { period: 'Bulan 3', baseline: 100, scenario: 70 },
        { period: 'Bulan 4', baseline: 100, scenario: 45 },
        { period: 'Bulan 5', baseline: 100, scenario: 30 },
        { period: 'Bulan 6', baseline: 100, scenario: 15 }
      ],
      interpretation: 'Jika 1 dari 3 pemasok utama berhenti memasok, kapasitas produksi akan turun drastis dalam 4 bulan. Tanpa diversifikasi pemasok, bisnis berisiko kolaps total dalam 6 bulan.'
    },
    {
      type: 'cashflow',
      title: 'Simulasi Guncangan Cashflow',
      probability: 72,
      impactTime: '2-3 bulan',
      estimatedLoss: 'Rp 280 juta',
      chartType: 'line',
      chartData: [
        { period: 'Bulan 1', baseline: 50, scenario: 50 },
        { period: 'Bulan 2', baseline: 55, scenario: 20 },
        { period: 'Bulan 3', baseline: 60, scenario: -15 },
        { period: 'Bulan 4', baseline: 65, scenario: -40 },
        { period: 'Bulan 5', baseline: 70, scenario: -55 },
        { period: 'Bulan 6', baseline: 75, scenario: -70 }
      ],
      interpretation: 'Jika terjadi penundaan pembayaran dari buyer utama selama 30 hari, cashflow akan negatif dalam 2 bulan. Buffer keuangan tidak cukup untuk menutupi operasional lebih dari 60 hari.'
    },
    {
      type: 'operational',
      title: 'Simulasi Kegagalan Operasional',
      probability: 55,
      impactTime: '1-2 bulan',
      estimatedLoss: 'Rp 180 juta',
      chartType: 'bar',
      chartData: [
        { category: 'Breakdown Mesin', impact: 85 },
        { category: 'Kualitas Produk', impact: 65 },
        { category: 'Delay Produksi', impact: 75 },
        { category: 'Waste Material', impact: 45 },
        { category: 'Kehilangan Order', impact: 90 }
      ],
      interpretation: 'Jika mesin pasteurisasi mengalami breakdown, dampak langsung adalah penghentian produksi total. Tanpa backup equipment, kerugian mencapai Rp 6 juta per hari downtime.'
    }
  ];

  const criticalAlerts = [
    {
      id: 1,
      severity: 'critical',
      title: 'Konsentrasi Pemasok Ekstrem Terdeteksi',
      description: 'AI mendeteksi 78% total pasokan susu berasal dari hanya 3 pemasok. Pola ini menunjukkan risiko supply chain collapse yang sangat tinggi jika salah satu pemasok berhenti beroperasi.',
      area: 'Manajemen Pasokan',
      confidence: 94,
      detectedAt: '2 jam yang lalu',
      recommendation: 'Segera diversifikasi pemasok dengan menambah minimal 5 pemasok baru dalam 60 hari. Implementasikan kontrak jangka panjang dengan klausul penalty untuk menjaga stabilitas pasokan.'
    },
    {
      id: 2,
      severity: 'critical',
      title: 'Pola Cashflow Negatif Berulang',
      description: 'Sistem mendeteksi cashflow negatif terjadi 3 dari 6 bulan terakhir dengan tren memburuk. Pola ini mengindikasikan masalah struktural dalam manajemen keuangan, bukan fluktuasi musiman.',
      area: 'Kesehatan Keuangan',
      confidence: 91,
      detectedAt: '3 jam yang lalu',
      recommendation: 'Renegosiasi term pembayaran dengan buyer untuk mempercepat cash collection. Implementasikan sistem early warning untuk monitoring cashflow harian.'
    },
    {
      id: 3,
      severity: 'high',
      title: 'Equipment Aging Risk Meningkat',
      description: 'Mesin pasteurisasi utama berusia 8 tahun tanpa major overhaul. AI memprediksi probabilitas breakdown 45% dalam 12 bulan berdasarkan pola maintenance history dan usage intensity.',
      area: 'Infrastruktur Peralatan',
      confidence: 87,
      detectedAt: '5 jam yang lalu',
      recommendation: 'Alokasikan budget Rp 150 juta untuk overhaul mesin atau pertimbangkan replacement. Implementasikan preventive maintenance schedule yang ketat.'
    },
    {
      id: 4,
      severity: 'high',
      title: 'Ketergantungan Buyer Institusional Tinggi',
      description: 'Analisis menunjukkan 70% revenue berasal dari 2 buyer institusional. Kehilangan salah satu kontrak akan menyebabkan penurunan revenue 35% dengan dampak langsung pada cashflow.',
      area: 'Diversifikasi Pasar',
      confidence: 89,
      detectedAt: '6 jam yang lalu',
      recommendation: 'Kembangkan channel retail dan B2C untuk mengurangi ketergantungan. Target minimal 40% revenue dari non-institutional buyers dalam 6 bulan.'
    },
    {
      id: 5,
      severity: 'medium',
      title: 'Gap Sistem Quality Control',
      description: 'Tidak ditemukan dokumentasi SOP quality control yang terstandar. Rejection rate 12% menunjukkan inkonsistensi kualitas produk yang dapat berdampak pada reputasi dan kontrak jangka panjang.',
      area: 'Tata Kelola Operasional',
      confidence: 82,
      detectedAt: '8 jam yang lalu',
      recommendation: 'Implementasikan sistem QC berbasis ISO dengan dokumentasi lengkap. Training staff untuk standardisasi proses quality checking.'
    }
  ];

  const trendData = {
    current: [
      { month: 'Jan', supply: 45, operational: 38, financial: 42, market: 35 },
      { month: 'Feb', supply: 52, operational: 41, financial: 45, market: 38 },
      { month: 'Mar', supply: 58, operational: 45, financial: 48, market: 40 },
      { month: 'Apr', supply: 65, operational: 48, financial: 52, market: 42 },
      { month: 'Mei', supply: 72, operational: 52, financial: 58, market: 45 },
      { month: 'Jun', supply: 72, operational: 58, financial: 64, market: 45 }
    ],
    optimistic: [
      { month: 'Jan', supply: 45, operational: 38, financial: 42, market: 35 },
      { month: 'Feb', supply: 48, operational: 40, financial: 44, market: 37 },
      { month: 'Mar', supply: 50, operational: 42, financial: 46, market: 39 },
      { month: 'Apr', supply: 52, operational: 44, financial: 48, market: 41 },
      { month: 'Mei', supply: 54, operational: 46, financial: 50, market: 43 },
      { month: 'Jun', supply: 55, operational: 48, financial: 52, market: 45 }
    ],
    pessimistic: [
      { month: 'Jan', supply: 45, operational: 38, financial: 42, market: 35 },
      { month: 'Feb', supply: 55, operational: 43, financial: 47, market: 39 },
      { month: 'Mar', supply: 65, operational: 48, financial: 52, market: 43 },
      { month: 'Apr', supply: 75, operational: 53, financial: 58, market: 47 },
      { month: 'Mei', supply: 82, operational: 58, financial: 65, market: 51 },
      { month: 'Jun', supply: 88, operational: 63, financial: 72, market: 55 }
    ]
  };

  const scenarios = [
    { id: 'current', label: 'Skenario Saat Ini' },
    { id: 'optimistic', label: 'Skenario Optimis' },
    { id: 'pessimistic', label: 'Skenario Pesimis' }
  ];

  const hiddenRiskDetections = [
    {
      id: 1,
      pattern: 'Pola Fluktuasi Pasokan Tidak Normal',
      icon: 'TrendingDown',
      confidence: 92,
      description: 'AI mendeteksi pola fluktuasi pasokan susu yang tidak mengikuti seasonality normal industri dairy. Variasi 25% per bulan mengindikasikan masalah struktural dalam hubungan dengan pemasok, bukan faktor cuaca atau musiman.',
      dataSource: 'Data Pasokan 12 Bulan + Benchmark Industri',
      potentialImpact: 'Risiko supply shortage 68% dalam 6 bulan',
      indicators: ['Variasi Tinggi', 'Non-Seasonal', 'Trend Memburuk']
    },
    {
      id: 2,
      pattern: 'Korelasi Tersembunyi: Rejection Rate vs Payment Delay',
      icon: 'Link',
      confidence: 88,
      description: 'Sistem menemukan korelasi kuat (r=0.82) antara keterlambatan pembayaran ke pemasok dengan peningkatan rejection rate produk. Ini mengindikasikan pemasok mengirim susu kualitas rendah saat pembayaran tertunda.',
      dataSource: 'Data Keuangan + Data Quality Control',
      potentialImpact: 'Penurunan kualitas produk 15-20%',
      indicators: ['Korelasi Kuat', 'Quality Impact', 'Relationship Risk']
    },
    {
      id: 3,
      pattern: 'Margin Compression Pattern',
      icon: 'TrendingDown',
      confidence: 85,
      description: 'Analisis menunjukkan margin operasional terkompresi dari 12% ke 8% dalam 6 bulan tanpa peningkatan signifikan pada volume produksi. Pola ini mengindikasikan inefisiensi operasional atau kenaikan biaya yang tidak terkontrol.',
      dataSource: 'Data Keuangan + Data Produksi',
      potentialImpact: 'Cashflow negatif dalam 3-4 bulan',
      indicators: ['Margin Decline', 'Cost Inefficiency', 'Volume Stagnant']
    },
    {
      id: 4,
      pattern: 'Dependency Concentration Risk',
      icon: 'Users',
      confidence: 90,
      description: 'AI mengidentifikasi triple concentration risk: 78% pasokan dari 3 pemasok, 70% revenue dari 2 buyer, dan 85% produksi bergantung pada 1 mesin utama. Kombinasi ini menciptakan perfect storm scenario.',
      dataSource: 'Data Pemasok + Data Penjualan + Data Peralatan',
      potentialImpact: 'Business collapse risk jika 1 faktor gagal',
      indicators: ['Triple Risk', 'High Concentration', 'Single Point Failure']
    }
  ];

  const handleDrillDown = (riskId) => {
    console.log('Drilling down into risk:', riskId);
  };

  const handleProceedToReport = () => {
    navigate('/executive-report');
  };

  if (isProcessing || !showDashboard) {
    return (
      <AIProcessingStatus
        isProcessing={isProcessing}
        progress={processingProgress}
        stage={processingStage}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-accent/10">
      <Header />
      <ProgressIndicator currentStep={9} totalSteps={9} />

      {isProcessing && (
        <div className="container mx-auto px-4 py-8 md:py-12">
          <div className="max-w-2xl mx-auto">
            <div className="card shadow-2xl border-2 border-primary/20 bg-gradient-to-br from-card to-primary/5">
              <div className="text-center mb-8">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg animate-pulse">
                  <Icon name="Brain" size={40} className="text-primary-foreground" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  AI Sedang Menganalisis Data Anda
                </h2>
                <p className="text-base md:text-lg text-muted-foreground">
                  Sistem AI kami sedang memproses semua data untuk menghasilkan insight mendalam
                </p>
              </div>

              <AIProcessingStatus
                progress={processingProgress}
                stage={processingStage}
              />
            </div>
          </div>
        </div>
      )}

      {showDashboard && (
        <div className="container mx-auto px-4 py-8 md:py-12 space-y-8 animate-fade-in">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-success/20 to-primary/20 border-2 border-success/30 mb-4 shadow-lg">
              <div className="w-3 h-3 rounded-full bg-success animate-pulse shadow-lg shadow-success/50" />
              <span className="text-sm font-semibold text-success">Analisis AI Selesai</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Dashboard Analisis AI
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Insight komprehensif berbasis AI untuk mendukung keputusan scale-up bisnis Anda
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="transform hover:scale-[1.02] transition-transform duration-300">
              <ReadinessScore score={38} breakdown={readinessBreakdown} />
            </div>
            <div className="transform hover:scale-[1.02] transition-transform duration-300">
              <RiskHeatmap riskData={riskData} onDrillDown={(id) => console.log('Drill down:', id)} />
            </div>
          </div>

          <div className="transform hover:scale-[1.01] transition-transform duration-300">
            <CriticalAlerts alerts={criticalAlerts} />
          </div>

          <div className="transform hover:scale-[1.01] transition-transform duration-300">
            <SimulationResults simulations={simulationData} />
          </div>

          <div className="transform hover:scale-[1.01] transition-transform duration-300">
            <RiskTrendChart trendData={trendData} scenarios={scenarios} />
          </div>

          <div className="transform hover:scale-[1.01] transition-transform duration-300">
            <HiddenRiskDetection detections={hiddenRiskDetections} />
          </div>

          <div className="card bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 border-2 border-primary/30 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg">
                <Icon name="FileText" size={32} className="text-primary-foreground" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-3">Siap Melihat Laporan Lengkap?</h3>
              <p className="text-base md:text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
                Dapatkan executive report komprehensif dengan rekomendasi strategis dan action plan terstruktur
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  onClick={() => navigate('/executive-report')}
                  className="w-full sm:w-auto px-8 py-4 text-lg font-semibold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 bg-gradient-to-r from-primary to-accent"
                >
                  <Icon name="FileText" size={20} className="mr-2" />
                  Lihat Executive Report
                </Button>
                <Button
                  onClick={() => navigate('/action-plan-dashboard')}
                  variant="outline"
                  className="w-full sm:w-auto px-8 py-4 text-lg font-semibold border-2 hover:bg-primary/10 transform hover:scale-105 transition-all duration-300"
                >
                  <Icon name="CheckSquare" size={20} className="mr-2" />
                  Buat Action Plan
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAnalysisDashboard;